import { CHAPTERS } from '../data/site';
import ChapterShell from '../components/ChapterShell';
import LightboxImage from '../components/Lightbox';
import { WipeLink } from '../components/Wipe';
import { MaskedLines, FadeUp, Eyebrow } from '../components/Reveal';

const C = CHAPTERS[4];
const NAVY = '#01114F';

const STAGES = [
  {
    n: '1', layer: 'Physical Layer', t: 'Solve for Data', sub: 'The Icebreaker',
    d: 'The highest priority layer. Before any reform, the system must start recording its activity in a structured way, the hardest step, because it breaks long standing unstructured habits. A physical intervention accelerates this: tactile feedback, reduced loss of control, trust built through immediate visible outcomes.',
  },
  {
    n: '2', layer: 'Digital Layer', t: 'Solve for Actionable & Experience', sub: 'Experience & Action',
    d: 'Captured data translates into actionable insight, with a human in the loop. The owner remains the final decision point; the system surfaces what matters, reduces micro management, and systemizes operations and customer experience without adding complexity.',
  },
  {
    n: '3', layer: 'Systemic Layer', t: 'Solve for Expansion & Growth', sub: 'Grow and Grow',
    d: 'Once the first two layers perform consistently, growth follows: continuous market integration and expansion without disrupting the core. The system extends capabilities rather than altering its foundation, physical and digital finally aligned into one structured workflow.',
  },
];

const USAGE = [
  { t: 'Starting at the counter', s: 'Data Collection', d: 'The counter is where business actually happens, every transaction, stock movement and credit interaction. The system captures reality as it happens, instead of reconstructing it later.' },
  { t: 'Capturing actions as they happen', s: 'Data Organization', d: 'Not data entry, actions themselves become data points. Data emerges naturally from use, without adding effort or disrupting speed.' },
  { t: 'Bringing scattered information together', s: 'Analyze & Synthesize', d: 'Sales, stock, cash and credit begin to exist in one connected layer, reducing reliance on memory without changing how decisions are taken.' },
  { t: 'Supporting decisions, not replacing them', s: 'Convert into Actionable', d: 'The shopkeeper continues to make all decisions, but with better awareness. Signals and clarity, not instructions.' },
  { t: 'Letting patterns emerge over time', s: 'Develop Experience', d: 'Repeated actions turn into insights; behaviour becomes visible. The framework grows more valuable not instantly, but over time.' },
  { t: 'Building gradually with use', s: 'Consistent Delivery', d: 'The system grows with the shop, from visibility, to understanding, to better control. No complete change demanded upfront.' },
  { t: 'Adapting to different ways of working', s: 'Systemize Micro processes', d: 'Each shop operates differently. The framework aligns with existing behaviour rather than standardising it.' },
  { t: 'Enabling future connections', s: 'Integration & Expansion', d: 'Stabilise the internal system first, then suppliers, platforms and external systems become possible without losing control.' },
];

const ARTEFACTS = [
  { img: '/assets/book/strategy-diamond.png', t: 'Strategy Diamond', d: 'Where the solution operates, how it is delivered, what makes it different, how it grows, and how it creates value, arenas, vehicles, differentiators, staging, economic logic.' },
  { img: '/assets/book/system-map.png', t: 'System Map', d: 'How everyday actions at the counter move through the system, from capture to structuring, to signal generation, to decision support and growth.' },
  { img: '/assets/book/logic-map.png', t: 'Logic Map', d: 'The cause and effect chain: inputs → activities → outputs → short term visibility → long term outcomes like credit control and reduced inefficiency.' },
  { img: '/assets/book/touchpoint-matrix.png', t: 'Touchpoint Matrix', d: 'Every key moment across the solution’s lifecycle, where interaction happens, what the user expects, and how the three layers respond.' },
];

const Resolution = () => (
  <ChapterShell
    chapter={C}
    heroImg="/assets/book/framework-clean.png"
    heroCaption="The 3-Layer Approach, the outcome of this project"
    heroContain
    nextOverride={{ to: '/saarthi', label: 'Retail Saarthi', color: '#F9526B', eyebrow: 'Then, the framework, imagined' }}
  >

    {/* CONCEPT NOTE */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="resolution-concept">
      <Eyebrow color={NAVY}>05 · Concept Note</Eyebrow>
      <MaskedLines
        lines={['The outcome is not a product.', 'It is a framework,', 'and the system around it.']}
        className="mt-10 text-4xl md:text-7xl font-light tracking-tight leading-[1.05]"
      />
      <FadeUp delay={0.15}>
        <p className="mt-10 text-lg md:text-2xl font-normal leading-relaxed max-w-4xl text-ink/85">
          A strategic, systemic framework that helps small shop owners respond to growing
          competitive pressure by aligning operations with changing customer behaviour,
          enabling them to adapt, coexist, and collaborate within the evolving retail landscape.
          Built as three layers: a <span style={{ color: NAVY }} className="font-medium">physical interaction layer</span>,
          a <span style={{ color: NAVY }} className="font-medium"> digital intelligence layer</span>, and
          a <span style={{ color: NAVY }} className="font-medium"> systemic backbone</span>, structuring daily
          operations, enabling better decision making, and creating a foundation for continuous
          improvement, long term growth, and scalable adaptation.
        </p>
      </FadeUp>
    </section>

    {/* 3 LAYER FRAMEWORK */}
    <section className="bg-brand-navy text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="resolution-framework">
      <Eyebrow color="#FFB000">The 3-Layer Approach</Eyebrow>
      <MaskedLines
        lines={['Data first.', 'Then clarity.', 'Then growth.']}
        className="mt-10 text-4xl md:text-7xl font-light tracking-tight leading-[1.05]"
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/15 border border-white/15">
        {STAGES.map((s, i) => (
          <FadeUp key={i} delay={i * 0.08} className="relative bg-brand-navy p-8 md:p-10 overflow-hidden">
            <span className="absolute -top-6 -right-2 text-[9rem] font-light text-paper/[0.06] leading-none select-none">{s.n}</span>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-amber">Stage {s.n} · {s.layer}</p>
            <p className="relative mt-6 text-2xl md:text-3xl font-light tracking-tight leading-tight">{s.t}</p>
            <p className="relative mt-2 text-[11px] uppercase tracking-[0.25em] text-paper/40">{s.sub}</p>
            <p className="relative mt-5 text-sm md:text-base text-paper/65 leading-relaxed">{s.d}</p>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2}>
        <LightboxImage
          src="/assets/book/framework-detailed.png"
          alt="The 3-Layer Approach framework with the eight step flow"
          testId="framework-diagram"
          caption="The 3-Layer Approach with the eight step flow, current version"
          className="mt-14 border border-white/15"
        />
        <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-paper/40">The framework pyramid, tap to study full screen</p>
      </FadeUp>
    </section>

    {/* FUNCTIONAL ARCHITECTURE */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36 border-b border-ink/10" data-testid="resolution-architecture">
      <Eyebrow color={NAVY}>Functional Architecture</Eyebrow>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-5">
          <MaskedLines
            lines={['If the pyramid', 'is the strategy,', 'this is the plumbing.']}
            className="text-4xl md:text-5xl font-light tracking-tight leading-[1.05]"
          />
          <FadeUp delay={0.15}>
            <p className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed">
              The architecture maps how each stage actually behaves. Stage 1 defines its data
              expectations, touchpoints, user experience and important considerations, partial
              entries, speed first, plug and play. An AI layer sits between the stages,
              translating captured data through a custom library.
            </p>
            <p className="mt-5 text-base md:text-lg text-ink/70 leading-relaxed">
              Stage 2 turns actionables into experience, trust and output delivery, with
              explainable reasoning and the owner keeping final decision power. Stage 3
              compounds everything into shared logic, a replicable model, and a growth pathway
              where new hires and new locations inherit the same structure.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.1} className="lg:col-span-7">
          <LightboxImage
            src="/assets/book/functional-architecture.png"
            alt="Functional architecture of the 3-Layer Approach"
            testId="architecture-diagram"
            caption="Functional architecture, how the three stages behave and connect"
            className="border border-ink/15"
          />
          <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-support">The working map behind the pyramid, tap to study full screen</p>
        </FadeUp>
      </div>
    </section>

    {/* HOW ITS USED, IMPLEMENTATION */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="resolution-implementation">
      <Eyebrow color={NAVY}>How the Framework is Used, Implementation</Eyebrow>
      <MaskedLines
        lines={['Strengthen what exists.', 'Replace nothing.']}
        className="mt-10 text-4xl md:text-6xl font-light tracking-tight leading-[1.02]"
      />
      <div className="mt-14">
        {USAGE.map((u, i) => (
          <FadeUp key={i} y={20}>
            <div className="group grid grid-cols-12 items-baseline gap-4 border-t border-ink/10 py-8 transition-colors duration-300 last:border-b" data-testid={`implementation-step-${i + 1}`}>
              <span className="col-span-2 md:col-span-1 text-3xl md:text-4xl font-light tracking-tight text-ink/20 group-hover:text-brand-navy transition-colors duration-300">
                {i + 1}
              </span>
              <div className="col-span-10 md:col-span-4">
                <p className="text-xl md:text-2xl font-medium tracking-tight">{u.t}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-support">{u.s}</p>
              </div>
              <p className="col-span-12 md:col-span-7 text-sm md:text-base text-ink/65 leading-relaxed">{u.d}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* STRATEGY ARTEFACTS */}
    <section className="px-5 md:px-10 lg:px-24 pb-24 md:pb-36" data-testid="resolution-artefacts">
      <Eyebrow color={NAVY}>The System Around the Framework</Eyebrow>
      <FadeUp delay={0.08}>
        <p className="mt-8 text-lg md:text-2xl font-normal leading-relaxed max-w-3xl text-ink/85">
          Four artefacts keep every decision aligned, how the framework is positioned, how it
          flows, why each part exists, and where it must respond.
        </p>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10">
        {ARTEFACTS.map((a, i) => (
          <FadeUp key={i} delay={i * 0.06}>
            <figure data-testid={`artefact-${a.t.toLowerCase().replace(/\s/g, '-')}`}>
              <LightboxImage
                src={a.img}
                alt={a.t}
                caption={`${a.t}, strategy artefact from the book`}
                testId={`artefact-img-${a.t.toLowerCase().replace(/\s/g, '-')}`}
                className="border border-ink/15"
              />
              <figcaption className="mt-4">
                <p className="text-lg font-medium tracking-tight">{a.t}</p>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{a.d}</p>
              </figcaption>
            </figure>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* BRIDGE TO CASE STUDY */}
    <section className="text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" style={{ background: '#F9526B' }} data-testid="resolution-bridge">
      <Eyebrow color="#FAFAF7">From Framework to Form</Eyebrow>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mt-10">
        <div className="lg:col-span-7">
          <MaskedLines
            lines={['But what could this', 'framework look like', 'in a real shop?']}
            className="text-4xl md:text-6xl font-light tracking-tight leading-[1.05]"
          />
          <FadeUp delay={0.2}>
            <p className="mt-8 text-paper/85 text-base md:text-lg leading-relaxed max-w-xl">
              Retail Saarthi is a conceptual case study, one imagination of how the 3-Layer
              Approach and its system could be developed into a tangible product service system.
              Not the outcome itself, a way to see the outcome.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <WipeLink
              to="/saarthi"
              data-testid="case-study-cta"
              className="group mt-10 inline-flex items-center gap-4 border border-paper/50 px-8 py-4 text-sm font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-paper hover:text-brand-pink"
            >
              Enter the case study
              <span className="transition-transform duration-500 group-hover:translate-x-2">→</span>
            </WipeLink>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="lg:col-span-5">
          <img
            src="/assets/book/img-p254-46613.jpg"
            alt="Retail Saarthi conceptual device family"
            className="w-full aspect-[4/3] object-cover shadow-2xl"
          />
          <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-paper/60">Concept visualisation, AI courtesy Vizcom</p>
        </FadeUp>
      </div>
    </section>

    {/* CLOSING */}
    <section className="bg-brand-navy text-paper px-5 md:px-10 lg:px-24 py-28 md:py-40 text-left" data-testid="resolution-closing">
      <MaskedLines
        lines={['"All limitations', 'are self imposed."']}
        className="text-5xl md:text-8xl font-light tracking-tight leading-[0.98]"
      />
      <FadeUp delay={0.25}>
        <p className="mt-6 text-sm uppercase tracking-[0.3em] text-paper/50">Charles Holmes</p>
        <p className="mt-16 text-paper/60 text-base md:text-lg leading-relaxed max-w-2xl">
          This is not the end. It is not even the beginning of the end. But it is, perhaps,
          the end of the beginning.
        </p>
        <p className="mt-4 text-xs uppercase tracking-[0.3em] text-paper/40">Winston Churchill</p>
      </FadeUp>
    </section>
  </ChapterShell>
);

export default Resolution;
